
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    auth,
    db,
    serverTimestamp,
    GoogleAuthProvider,
    OAuthProvider,
    type UserProfileData
} from '../firebase.ts';
import { CloseIcon, GoogleIcon, AppleIcon } from '../constants.tsx';
import { trackTikTokEvent } from '../tiktok.ts';

// --- DEBUG: Log module execution start ---
console.log('[AuthModal.tsx] Module script loaded.');

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialView: 'signIn' | 'signUp';
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialView }) => {
    // --- DEBUG: Log component render ---
    console.log(`[AuthModal] Component rendering or re-rendering. isOpen: ${isOpen}, initialView: ${initialView}`);
    
    const { t } = useTranslation();
    const [view, setView] = useState(initialView);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // --- DEBUG: Log view change from props ---
        console.log(`[AuthModal] useEffect (initialView) running. Setting view to: ${initialView}`);
        setView(initialView);
    }, [initialView]);
    
    useEffect(() => {
      // --- DEBUG: Log state reset ---
      console.log(`[AuthModal] useEffect (isOpen, view) running. Resetting form state. View: ${view}`);
      setError(null);
      setName('');
      setEmail('');
      setPhoneNumber('');
      setPassword('');
      setConfirmPassword('');
    }, [isOpen, view]);

    if (!isOpen) {
        // --- DEBUG: Log component returning null ---
        console.log('[AuthModal] isOpen is false, rendering null.');
        return null;
    }

    const handleAuthError = (err: unknown) => {
        console.error('❌ [AuthModal] Authentication Error Occurred. Raw error:', err);
        setLoading(false);
        if (typeof err === 'object' && err !== null && 'code' in err) {
            // FIX: Use a structural type for the error object to resolve typing issues.
            // The imported `FirebaseError` type was causing conflicting errors, and this approach is more robust.
            const firebaseError = err as { code: string };
            let errorMessage = '';
            switch (firebaseError.code) {
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email format.';
                    break;
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                     errorMessage = 'Invalid email or password.';
                    break;
                case 'auth/email-already-in-use':
                    errorMessage = 'An account with this email already exists.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password should be at least 6 characters.';
                    break;
                default:
                    errorMessage = t('authModalError');
            }
            console.log(`[AuthModal] Mapped Firebase error code ${firebaseError.code} to message: "${errorMessage}"`);
            setError(errorMessage);
        } else {
            console.log('[AuthModal] Could not map error, using generic message.');
            setError(t('authModalError'));
        }
    };

    const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        console.log(`[AuthModal] Handling Email/Password Submit for view: ${view}`);
        
        try {
            if (view === 'signUp') {
                if (password !== confirmPassword) {
                    console.warn('[AuthModal] Sign up validation failed: Passwords do not match.');
                    setError(t('authModalPasswordMismatchError'));
                    setLoading(false);
                    return;
                }
                console.log('[AuthModal] Calling createUserWithEmailAndPassword...');
                const userCredential = await auth.createUserWithEmailAndPassword(email, password);
                const user = userCredential.user;
                console.log('✅ [AuthModal] User created successfully. User:', user);
                
                // It's still good practice to update the Auth profile itself.
                await user.updateProfile({ displayName: name });
                console.log('[AuthModal] Auth profile updated with displayName:', name);

                // Immediately create the user document in Firestore to avoid race conditions.
                const userDocRef = db.collection('users').doc(user.uid);
                
                const newProfile: UserProfileData = {
                    displayName: name,
                    email: email,
                    phoneNumber: phoneNumber || null,
                    scar: false,
                    admin: false,
                    createdAt: serverTimestamp(),
                };
                
                await userDocRef.set(newProfile);
                console.log('✅ [AuthModal] Firestore user profile created:', newProfile);

                trackTikTokEvent('CompleteRegistration', {
                    content_name: 'Sign Up',
                }, {
                    email: email,
                    phone: phoneNumber || undefined
                });
                
            } else {
                console.log('[AuthModal] Calling signInWithEmailAndPassword...');
                await auth.signInWithEmailAndPassword(email, password);
                console.log('✅ [AuthModal] User signed in successfully.');
            }
            onClose(); // This will trigger the onAuthStateChanged listener in App.tsx
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSocialLogin = async (provider: 'google' | 'apple') => {
        setLoading(true);
        setError(null);
        console.log(`[AuthModal] Handling Social Login with provider: ${provider}`);
        const authProvider = provider === 'google' 
            ? new GoogleAuthProvider() 
            : new OAuthProvider('apple.com');
            
        try {
            console.log(`[AuthModal] Calling signInWithPopup for ${provider}...`);
            await auth.signInWithPopup(authProvider);
            console.log(`✅ [AuthModal] Social login with ${provider} successful.`);
            onClose(); // This will trigger the onAuthStateChanged listener in App.tsx
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    }
    
    const toggleView = () => {
        const newView = view === 'signIn' ? 'signUp' : 'signIn';
        console.log(`[AuthModal] Toggling view from ${view} to ${newView}`);
        setView(newView);
    };

    return (
        <div className="auth-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="auth-modal-content p-8 sm:p-10" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="auth-modal-close" aria-label={t('close')}>
                    <CloseIcon />
                </button>
                
                <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
                    {view === 'signIn' ? t('authModalSignInTitle') : t('authModalSignUpTitle')}
                </h2>
                <p className="text-center text-white/60 mb-8">
                    {view === 'signIn' ? t('authModalNoAccount') : t('authModalExistingAccount')}{' '}
                    <button onClick={toggleView} className="font-semibold text-purple-400 hover:text-purple-300 transition">
                        {view === 'signIn' ? t('signUp') : t('signIn')}
                    </button>
                </p>

                <form onSubmit={handleEmailPasswordSubmit} className="space-y-5">
                    {view === 'signUp' && (
                        <div>
                            <label className="text-sm font-medium text-white/80 block mb-2">{t('authModalNameLabel')}</label>
                            <input 
                                type="text" 
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t('authModalNamePlaceholder')}
                                className="w-full rounded-lg p-3 outline-none auth-input transition"
                                required
                            />
                        </div>
                    )}
                    <div>
                        <label className="text-sm font-medium text-white/80 block mb-2">{t('authModalEmailLabel')}</label>
                        <input 
                            type="email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full rounded-lg p-3 outline-none auth-input transition"
                            required
                        />
                    </div>
                     {view === 'signUp' && (
                        <div>
                            <label className="text-sm font-medium text-white/80 block mb-2">{t('authModalPhoneLabel')}</label>
                            <input 
                                type="tel" 
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder={t('authModalPhonePlaceholder')}
                                className="w-full rounded-lg p-3 outline-none auth-input transition"
                            />
                        </div>
                    )}
                     <div>
                        <label className="text-sm font-medium text-white/80 block mb-2">{t('authModalPasswordLabel')}</label>
                        <input 
                            type="password" 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-lg p-3 outline-none auth-input transition"
                            required
                        />
                    </div>
                    {view === 'signUp' && (
                        <div>
                            <label className="text-sm font-medium text-white/80 block mb-2">{t('authModalConfirmPasswordLabel')}</label>
                            <input 
                                type="password" 
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full rounded-lg p-3 outline-none auth-input transition"
                                required
                            />
                        </div>
                    )}
                    {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                    <button type="submit" className="hero-button w-full rounded-lg py-3.5 font-semibold text-white transition disabled:opacity-50" disabled={loading}>
                        {loading ? t('loading') : (view === 'signIn' ? t('authModalSignInButton') : t('authModalSignUpButton'))}
                    </button>
                </form>

                <div className="flex items-center my-6">
                    <div className="flex-grow h-px bg-white/10"></div>
                    <span className="mx-4 text-sm text-white/50">{t('authModalOrContinueWith')}</span>
                    <div className="flex-grow h-px bg-white/10"></div>
                </div>

                <div className="space-y-4">
                     <button onClick={() => handleSocialLogin('google')} className="social-btn w-full flex items-center justify-center gap-3 py-3 rounded-lg text-white font-semibold transition" disabled={loading}>
                        <GoogleIcon />
                        {t('authModalGoogleButton')}
                    </button>
                     <button onClick={() => handleSocialLogin('apple')} className="social-btn w-full flex items-center justify-center gap-3 py-3 rounded-lg text-white font-semibold transition" disabled={loading}>
                        <AppleIcon />
                        {t('authModalAppleButton')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AuthModal;